import i18n from "@/lib/i18n";
import router from "@/router";
import { showNotifyDialog, showOkCancelDialog } from "@/lib/dialog";
import { useThemeStore } from "@/stores/theme";
import { usePageEditStore } from "@/stores/pageEdit";
import type { Detail } from "@/lib/models/theme";
import type { Novel } from "@/lib/models/novel";
import { isScriptModeNovel } from "@/lib/models/novel";
import {
  FAN_FICTION_CATEGORY_ID,
  MATCHING_THEME_REQUIRED_FIELDS,
  THEME_FIELD_LABEL_KEYS,
} from "@/constants/themeMatching";
import { logError } from "@/lib/utils/logger";

export type NovelLike = Pick<Novel, "genre" | "category" | "manuscriptMode">;

// manuscriptMode === "script" の判定は novelModule/isScriptMode getter と共通化するため
// src/lib/models/novel.ts の isScriptModeNovel に集約している（ここでは re-export のみ）
export { isScriptModeNovel };

/** 作品のジャンル・カテゴリが設定済みかどうか */
export function isNovelInfoFilled(novel: NovelLike | null | undefined): boolean {
  return !!novel && !!novel.genre && !!novel.category && novel.category.length > 0;
}

/** 二次創作カテゴリが選ばれている作品は企画マッチング対象外 */
export function isFanFictionNovel(novel: NovelLike | null | undefined): boolean {
  return !!novel?.category?.includes(FAN_FICTION_CATEGORY_ID);
}

/** 未記入の必須項目の日本語ラベル一覧を返す（空なら全項目記入済み） */
export function getMissingRequiredFieldLabels(details: Detail[]): string[] {
  return MATCHING_THEME_REQUIRED_FIELDS.filter((name) => {
    const value = details.find((detail) => detail.name === name)?.value;
    return !value || !value.trim();
  }).map((name) => i18n.t(THEME_FIELD_LABEL_KEYS[name]) as string);
}

/**
 * 企画マッチング依頼前のバリデーション
 *
 * 1. 未保存の編集がある場合は保存を促して停止
 * 2. 脚本モード → 対象外として停止（ジャンル・カテゴリに依存しないため先に判定する）
 * 3. 作品のジャンル・カテゴリ未設定 → アラート + 作品情報編集ページへの誘導
 * 4. 二次創作カテゴリ → 対象外として停止
 * 5. theme 必須項目（企画用あらすじ）の未記入 → アラート
 *
 * いずれも NG の場合はダイアログを表示して false を返す（確認画面へは遷移しない）。
 */
export function useThemeMatchingValidation() {
  async function validate(options: { novelId: string; novel: NovelLike | null | undefined }): Promise<boolean> {
    const { novelId, novel } = options;

    // (0) 未保存の変更は判定対象にできないため、先に保存を促す
    const pageEditStore = usePageEditStore();
    if (pageEditStore.isModified) {
      await showNotifyDialog({
        title: i18n.t("themeMatching.validation.unsavedTitle") as string,
        content: i18n.t("themeMatching.validation.unsavedMessage") as string,
      });
      return false;
    }

    // (1) 脚本モードは企画マッチング対象外（ボタン非表示の保険。ジャンル・カテゴリ未設定でも判定できるため先に見る）
    if (isScriptModeNovel(novel)) {
      await showNotifyDialog({
        title: i18n.t("themeMatching.validation.scriptModeTitle") as string,
        content: i18n.t("themeMatching.validation.scriptModeDisabled") as string,
      });
      return false;
    }

    // (2) 作品のジャンル・カテゴリ（theme 項目より先にチェック）
    if (!isNovelInfoFilled(novel)) {
      const goToEdit = await showOkCancelDialog({
        title: i18n.t("themeMatching.validation.novelInfoTitle") as string,
        content: i18n.t("themeMatching.validation.novelInfoMessage") as string,
        okButton: i18n.t("themeMatching.validation.novelInfoEditButton") as string,
        cancelButton: i18n.t("common.close") as string,
        okVariant: "primary",
      });
      if (goToEdit) {
        router.push({ name: "detailEdit", params: { novelId } });
      }
      return false;
    }

    // (2.5) 二次創作は企画マッチング対象外（ボタン disabled の保険）
    if (isFanFictionNovel(novel)) {
      await showNotifyDialog({
        title: i18n.t("themeMatching.validation.fanFictionTitle") as string,
        content: i18n.t("themeMatching.validation.fanFictionDisabled") as string,
      });
      return false;
    }

    // (3) theme の必須項目（企画用あらすじ。保存済みデータで判定）
    const themeStore = useThemeStore();
    try {
      if (themeStore.theme?.novelId !== novelId) {
        await themeStore.fetchTheme(novelId);
      }
    } catch (error) {
      logError({ module: "useThemeMatchingValidation", action: "validate" }, "theme データの取得に失敗", error);
      await showNotifyDialog({
        content: i18n.t("themeMatching.validation.fetchErrorMessage") as string,
      });
      return false;
    }

    const missingLabels = getMissingRequiredFieldLabels(themeStore.theme?.detail ?? []);
    if (missingLabels.length > 0) {
      await showNotifyDialog({
        title: i18n.t("themeMatching.validation.requiredFieldsTitle") as string,
        content: i18n.t("themeMatching.validation.requiredFieldsMessage", {
          fields: missingLabels.map((label) => `「${label}」`).join(""),
        }) as string,
      });
      return false;
    }

    return true;
  }

  return { validate };
}
